// Servicio de API para comunicación con el backend
import API_CONFIG from '../config/api';

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Realiza una petición HTTP al backend
 * @param {string} endpoint - Endpoint relativo (ej: '/api/auth/login')
 * @param {object} options - Opciones de fetch (method, body, headers, etc.)
 * @returns {Promise<Response>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  // Headers por defecto
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  // Agregar token si existe
  const token = getAccessToken();
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  } else {
    if (isDev) {
      console.warn('⚠️ No se encontró token en localStorage. Verifica que el login se haya completado correctamente.');
    }
    // Si no hay token y es una petición que requiere autenticación, lanzar error más claro
    if (endpoint.includes('/pagos/') || endpoint.includes('/admin/') || endpoint.includes('/ecommerce/pedidos')) {
      throw new Error('Token faltante: Por favor inicia sesión nuevamente.');
    }
  }
  
  // Combinar headers
  const headers = {
    ...defaultHeaders,
    ...options.headers,
  };
  
    // Configuración de la petición
    // IMPORTANTE: Configurar explícitamente CORS para evitar problemas del navegador
    
    // Configurar timeout si está disponible
    let timeoutId;
    let controller;
    if (API_CONFIG.TIMEOUT && typeof AbortController !== 'undefined' && !options.signal) {
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        controller.abort();
      }, API_CONFIG.TIMEOUT);
    }
    
    const config = {
      ...options,
      headers,
      // Configuración explícita de CORS
      mode: 'cors',  // Permitir CORS
      credentials: 'include',  // Incluir cookies/credenciales si es necesario
      cache: 'no-cache',  // No cachear peticiones
      // Usar el signal del controller de timeout o el que viene en options
      signal: controller ? controller.signal : options.signal
    };
  
  try {
    if (isDev) {
      console.log(`📡 Enviando petición ${config.method || 'GET'} a: ${url}`, {
        hasToken: !!headers['Authorization'],
        origin: window.location.origin
      });
    }

    const response = await fetch(url, config);

    // Limpiar timeout si se configuró
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (isDev) {
      console.log(`📥 Respuesta recibida de ${url}:`, {
        status: response.status,
        ok: response.ok
      });
    }
    
    // Manejar errores 503 (Service Temporarily Unavailable) específicamente
    if (response.status === 503) {
      const errorMsg = `El servidor backend no está disponible temporalmente (503 Service Temporarily Unavailable). Esto puede deberse a que el backend está en proceso de despliegue o no hay contenedores corriendo. Por favor intenta nuevamente en unos momentos.`;
      console.error(`❌ Error 503 en ${url}: Backend no disponible`);
      throw new Error(errorMsg);
    }
    
    // Si la respuesta no es OK, lanzar error
    if (!response.ok) {
      let errorData = {};
      try {
        const text = await response.text();
        errorData = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error al parsear respuesta de error:', e);
      }
      
      // Extraer mensaje de error del backend (preferir message/mensaje para ver el detalle real)
      let errorMessage = errorData.message || errorData.mensaje || errorData.error || errorData.msg || `Error ${response.status}: ${response.statusText}`;
      
      // Si el error contiene detalles de SQLAlchemy/PostgreSQL, intentar extraer información útil
      if (errorMessage.includes('UndefinedColumn')) {
        const columnMatch = errorMessage.match(/column\s+(\w+\.\w+)\s+does\s+not\s+exist/i);
        if (columnMatch) {
          errorMessage = `Error de base de datos: la columna ${columnMatch[1]} no existe en la tabla. Este es un problema de configuración del backend.`;
        } else {
          errorMessage = 'Error de base de datos: la estructura de la tabla no coincide con lo esperado. Por favor contacta al administrador.';
        }
      } else if (errorMessage.includes('psycopg2') || errorMessage.includes('SQLAlchemy')) {
        // Errores de base de datos más generales
        if (errorMessage.includes('does not exist')) {
          errorMessage = 'Error de base de datos: un elemento requerido no existe. Por favor contacta al administrador.';
        }
      }
      
      console.error(`❌ Error ${response.status} en ${url}:`, errorData);
      
      // Si es error 401: intentar refrescar el token una vez antes de desloguear (excepto en el propio endpoint de refresh)
      if (response.status === 401) {
        const isRefreshEndpoint = endpoint === API_CONFIG.ENDPOINTS.AUTH.REFRESH || url.includes('/refresh');
        const alreadyRetried = options._retried === true;

        if (!isRefreshEndpoint && !alreadyRetried && getRefreshToken()) {
          try {
            const newToken = await refreshAccessTokenRaw();
            if (newToken) {
              // Reintentar la petición original con el nuevo token
              return apiRequest(endpoint, { ...options, _retried: true });
            }
          } catch (refreshErr) {
            console.warn('No se pudo renovar la sesión:', refreshErr.message);
          }
        }

        console.warn('⚠️ Error 401: Token inválido o faltante. Limpiando estado de autenticación.');
        clearTokens();
        window.dispatchEvent(new Event('userLogout'));
      }

      const err = new Error(errorMessage);
      if (errorData.error_code) {
        err.code = errorData.error_code;
      }
      throw err;
    }
    
    return response;
  } catch (error) {
    // Limpiar timeout si se configuró y hay un error
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    console.error(`💥 Error en petición a ${url}:`, {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    
    // Si es error de abort (timeout)
    if (error.name === 'AbortError') {
      const errorMsg = `Timeout: La petición a ${url} tardó más de ${API_CONFIG.TIMEOUT / 1000} segundos. El servidor puede estar sobrecargado o hay un problema de red.`;
      console.error('⏱️ Timeout en petición:', { url, timeout: API_CONFIG.TIMEOUT });
      throw new Error(errorMsg);
    }
    
    // Si es error de red, lanzar error más descriptivo
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      const baseUrl = API_CONFIG.BASE_URL;
      const isLocalhost = baseUrl.includes('localhost');
      
      // Intentar distinguir entre diferentes tipos de errores de red
      const errorMsgLower = error.message.toLowerCase();
      
      // Errores específicos de CORS (el navegador bloquea explícitamente por CORS)
      const isExplicitCORSError = errorMsgLower.includes('cors') || 
                                   errorMsgLower.includes('access-control-allow-origin') ||
                                   (errorMsgLower.includes('failed to fetch') && errorMsgLower.includes('cors'));
      
      // Errores de conexión general (servidor no responde, timeout, etc.)
      const isConnectionError = errorMsgLower.includes('failed to fetch') ||
                                errorMsgLower.includes('networkerror') ||
                                errorMsgLower.includes('network request failed') ||
                                errorMsgLower.includes('load failed');
      
      // Si es un error explícito de CORS
      if (isExplicitCORSError) {
        const errorMsg = `Error de CORS: El backend está bloqueando la petición desde ${window.location.origin}. Verifica que CORS esté configurado para permitir este origen en ${baseUrl}`;
        console.error('🚫 Error de CORS detectado:', {
          origin: window.location.origin,
          target: baseUrl,
          error: error.message
        });
        throw new Error(errorMsg);
      }
      
      // Si es un error de conexión general
      if (isConnectionError) {
        // Intentar verificar si el servidor está disponible haciendo un health check
        console.warn('⚠️ Error de conexión detectado. Verificando disponibilidad del servidor...');
        
        const errorMsg = isLocalhost
          ? `Error de conexión con el servidor (${baseUrl}). Asegúrate de que el backend esté corriendo en el puerto 5000.`
          : `Error de conexión con el servidor (${baseUrl}). El servidor puede estar temporalmente no disponible o hay un problema de red. Verifica tu conexión a internet.`;
        
        console.error('🚫 Error de conexión:', {
          origin: window.location.origin,
          target: baseUrl,
          endpoint: url,
          error: error.message
        });
        throw new Error(errorMsg);
      }
      
      // Error desconocido de red
      const errorMsg = `Error de red al conectar con ${baseUrl}: ${error.message}`;
      throw new Error(errorMsg);
    }
    throw error;
  }
};

/**
 * Realiza una petición GET
 */
export const apiGet = async (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'GET',
  });
};

/**
 * Realiza una petición POST
 */
export const apiPost = async (endpoint, data, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Realiza una petición PUT
 */
export const apiPut = async (endpoint, data, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Realiza una petición DELETE
 */
export const apiDelete = async (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'DELETE',
  });
};

/**
 * POST con FormData (archivos). No se setea Content-Type para que el navegador envíe multipart/form-data con boundary.
 * @param {string} endpoint - Ruta relativa
 * @param {File} file - Archivo
 * @param {string} fieldName - Nombre del campo (ej: 'file' o 'archivo')
 */
export const apiPostFormData = async (endpoint, file, fieldName = 'file', extraFields = null) => {
  const formData = new FormData();
  formData.append(fieldName, file);
  if (extraFields && typeof extraFields === 'object') {
    for (const [key, value] of Object.entries(extraFields)) {
      if (value !== undefined && value !== null) formData.append(key, String(value));
    }
  }
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  const token = getAccessToken();
  const headers = { Authorization: token ? `Bearer ${token}` : '' };
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    mode: 'cors',
    credentials: 'include',
  });
  return res;
};

/**
 * Obtiene el access token del localStorage
 */
export const getAccessToken = () => {
  const token = localStorage.getItem('access_token');
  if (isDev) {
    console.log('🔍 getAccessToken:', { tokenExists: !!token });
  }
  return token;
};

/**
 * Guarda el access token en localStorage
 */
export const setAccessToken = (token) => {
  localStorage.setItem('access_token', token);
};

/**
 * Obtiene el refresh token del localStorage
 */
export const getRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

/**
 * Guarda el refresh token en localStorage
 */
export const setRefreshToken = (token) => {
  localStorage.setItem('refresh_token', token);
};

/**
 * Limpia todos los tokens del localStorage
 */
export const clearTokens = () => {
  if (isDev) console.log('🧹 Limpiando tokens y estado de autenticación');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('user');
  localStorage.removeItem('isAdmin');
  if (isDev) console.log('✅ Tokens limpiados');
};

/**
 * Verifica si hay un token válido
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

/**
 * Refresca el access token con una petición directa (sin pasar por apiRequest)
 * para no disparar el manejo de 401. Usado internamente al recibir 401.
 */
const refreshAccessTokenRaw = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  const refreshUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.REFRESH}`;
  const res = await fetch(refreshUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${refreshToken}` },
    mode: 'cors',
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.success && data.data && data.data.access_token) {
    setAccessToken(data.data.access_token);
    return data.data.access_token;
  }
  throw new Error(data.error || data.message || 'Error al refrescar el token');
};

/**
 * Intenta refrescar el access token usando el refresh token
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No hay refresh token disponible');
  try {
    const newToken = await refreshAccessTokenRaw();
    return newToken;
  } catch (error) {
    clearTokens();
    throw error;
  }
};

